import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import axios from 'axios';
import QRCode from 'react-qr-code';
import {
  Upload, Users, FolderOpen, CreditCard, Trash2, Plus, LogOut,
  Home, FileText, Settings, UserPlus, Building2, BarChart3,
  Archive, Menu, Image as ImageIcon, ChevronDown, ChevronRight,
  Check, Lock, Unlock, Edit2, X, Briefcase, UsersRound, Award,
  GripVertical, ArrowUp, ArrowDown
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_blueprint-studio-34/artifacts/fe0mwn3b_image.png";

function AdminSidebar({ active, setActive, onLogout, mobileOpen, setMobileOpen }) {
  const items = [
    { id: 'overview', icon: BarChart3, label: 'Overview' },
    { id: 'projects', icon: FolderOpen, label: 'Projects' },
    { id: 'users', icon: Users, label: 'Users' },
    { id: 'vendors', icon: Building2, label: 'Vendors' },
    { id: 'payments', icon: CreditCard, label: 'Payments' },
    { id: 'gallery', icon: ImageIcon, label: 'Gallery' },
    { id: 'careers', icon: Briefcase, label: 'Careers' },
    { id: 'team', icon: UsersRound, label: 'Team' },
    { id: 'honors', icon: Award, label: 'Honors' },
    { id: 'settings', icon: Settings, label: 'Settings' },
  ];

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-6 border-b border-[#E5E5E5]">
        <a href="/" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="S.A.I." className="h-8" />
          <div>
            <span className="text-sm font-bold">S.A.I.</span>
            <span className="text-[10px] tracking-[0.2em] uppercase block text-[#5C5C5C] -mt-0.5">Admin Panel</span>
          </div>
        </a>
      </div>
      <div className="flex-1 py-4">
        {items.map(item => (
          <button key={item.id} data-testid={`admin-nav-${item.id}`}
            onClick={() => { setActive(item.id); setMobileOpen?.(false); }}
            className={`w-full flex items-center gap-3 px-6 py-3 text-sm transition-colors ${
              active === item.id ? 'bg-[#121212] text-white' : 'text-[#5C5C5C] hover:bg-[#F5F5F5] hover:text-[#121212]'
            }`}>
            <item.icon size={18} strokeWidth={1.25} />
            <span className="tracking-wide">{item.label}</span>
          </button>
        ))}
      </div>
      <div className="p-4 border-t border-[#E5E5E5]">
        <button onClick={onLogout} data-testid="admin-logout-btn"
          className="w-full flex items-center gap-3 px-6 py-3 text-sm text-[#5C5C5C] hover:bg-[#F5F5F5] hover:text-[#121212] transition-colors">
          <LogOut size={18} strokeWidth={1.25} /> <span>Logout</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      <aside className="hidden lg:block w-64 border-r border-[#E5E5E5] bg-white h-screen sticky top-0">
        <SidebarContent />
      </aside>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 bg-white h-full"><SidebarContent /></aside>
        </div>
      )}
    </>
  );
}

function OverviewTab({ projects, users, vendors, payments }) {  return (
    <div data-testid="admin-overview">
      <h2 className="text-xl font-bold mb-6">Dashboard Overview</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-1">
        {[
          { label: 'Active Projects', value: projects.filter(p => p.status === 'active').length },
          { label: 'Total Users', value: users.length },
          { label: 'Vendors', value: vendors.length },
          { label: 'Pending Payments', value: payments.filter(p => p.status === 'pending').length }
        ].map(stat => (
          <div key={stat.label} className="border border-[#E5E5E5] p-6 bg-white">
            <p className="text-xs tracking-[0.2em] uppercase text-[#5C5C5C] mb-2 font-bold">{stat.label}</p>
            <p className="text-3xl font-bold text-[#121212]">{stat.value}</p>
          </div>
        ))}
      </div>
      <div className="mt-8 border border-[#E5E5E5] bg-white">
        <div className="p-4 border-b border-[#E5E5E5]">
          <h3 className="text-sm font-bold uppercase tracking-wider">Recent Projects</h3>
        </div>
        {projects.slice(0, 5).map(proj => (
          <div key={proj.project_id} className="flex items-center justify-between p-4 border-b border-[#E5E5E5] last:border-0">
            <div>
              <p className="text-sm font-medium">{proj.title}</p>
              <p className="text-xs text-[#5C5C5C]">
                {(() => {
                  const ids = proj.client_ids?.length > 0 ? proj.client_ids : (proj.client_id ? [proj.client_id] : []);
                  const names = ids.map(id => users.find(u => u.user_id === id)?.name).filter(Boolean);
                  return names.length > 0 ? names.join(', ') : 'No client';
                })()} | {proj.phases?.length || 0} phases
              </p>
            </div>
            <Badge variant={proj.status === 'active' ? 'default' : 'secondary'} className="rounded-none text-[10px] uppercase tracking-wider">{proj.status}</Badge>
          </div>
        ))}
        {projects.length === 0 && <p className="p-6 text-sm text-[#5C5C5C] text-center">No projects yet</p>}
      </div>
    </div>
  );
}

// Phase Editor Component
function PhaseEditor({ project, onRefresh }) {
  const phases = project.phases || [];
  const [expandedPhase, setExpandedPhase] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [addingPhase, setAddingPhase] = useState(false);
  const [newPhase, setNewPhase] = useState({ name: '', deliverables: '', payment_amount: 0, notes: '' });
  const [editingName, setEditingName] = useState(null);
  const [tempName, setTempName] = useState('');
  const [fileDetails, setFileDetails] = useState({});
  const [ytEdit, setYtEdit] = useState(null); // { phaseNum, dIdx }
  const [ytForm, setYtForm] = useState({ name: '', url: '' });

  // Load file details for the project
  useEffect(() => {
    const loadFiles = async () => {
      try {
        const res = await axios.get(`${API}/projects/${project.project_id}/files`, { withCredentials: true });
        const map = {};
        res.data.forEach(f => { map[f.file_id] = f; });
        setFileDetails(map);
      } catch {}
    };
    loadFiles();
  }, [project]);

  const updatePhase = async (phaseNumber, updates) => {
    try {
      await axios.put(`${API}/projects/${project.project_id}/phases/${phaseNumber}`, updates, { withCredentials: true });
      onRefresh();
    } catch (err) { console.error(err); }
  };

  const uploadDeliverableFile = async (phaseNumber, deliverableIndex, file) => {
    setUploading(true);
    setUploadProgress(0);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(
        `${API}/projects/${project.project_id}/phases/${phaseNumber}/deliverables/${deliverableIndex}/upload`,
        fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded * 100) / e.total))
        }
      );
      onRefresh();
    } catch (err) { console.error(err); }
    finally { setUploading(false); setUploadProgress(0); }
  };

  const addPhase = async () => {
    const deliverables = newPhase.deliverables.split(',').map(d => d.trim()).filter(Boolean).map(name => ({
      name, status: 'pending', file_ids: []
    }));
    try {
      await axios.post(`${API}/projects/${project.project_id}/phases/add`, {
        name: newPhase.name,
        deliverables: deliverables.length > 0 ? deliverables : [{ name: 'Deliverable 1', status: 'pending', file_ids: [] }],
        payment_amount: parseFloat(newPhase.payment_amount) || 0,
        notes: newPhase.notes
      }, { withCredentials: true });
      setAddingPhase(false);
      setNewPhase({ name: '', deliverables: '', payment_amount: 0, notes: '' });
      onRefresh();
    } catch {}
  };

  const deletePhase = async (phaseNumber) => {
    try {
      await axios.delete(`${API}/projects/${project.project_id}/phases/${phaseNumber}`, { withCredentials: true });
      onRefresh();
    } catch {}
  };

  const movePhase = async (phaseNumber, direction) => {
    const currentOrder = phases.map(p => p.phase_number);
    const idx = currentOrder.indexOf(phaseNumber);
    if ((direction === 'up' && idx === 0) || (direction === 'down' && idx === currentOrder.length - 1)) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    [currentOrder[idx], currentOrder[swapIdx]] = [currentOrder[swapIdx], currentOrder[idx]];
    try {
      await axios.put(`${API}/projects/${project.project_id}/phases/reorder`, { phase_order: currentOrder }, { withCredentials: true });
      onRefresh();
    } catch {}
  };

  const renamephase = async (phaseNumber) => {
    if (tempName.trim()) {
      await updatePhase(phaseNumber, { name: tempName.trim() });
    }
    setEditingName(null);
    setTempName('');
  };

  const statusColor = (status) => {
    if (status === 'active') return 'bg-green-100 text-green-800';
    if (status === 'locked') return 'bg-gray-100 text-gray-600';
    if (status === 'completed') return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-600';
  };

  const paymentColor = (status) => {
    if (status === 'paid' || status === 'verified') return 'bg-green-100 text-green-800';
    if (status === 'pending') return 'bg-amber-100 text-amber-800';
    return 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="mt-4 space-y-1">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold uppercase tracking-wider">Project Phases</p>
        <button onClick={() => setAddingPhase(!addingPhase)} className="text-xs text-[#121212] border border-[#121212] px-3 py-1 hover:bg-[#121212] hover:text-white transition-colors" data-testid="add-phase-btn">
          <Plus size={10} className="inline mr-1" /> Add Phase
        </button>
      </div>

      {addingPhase && (
        <div className="border border-[#E5E5E5] p-4 bg-[#F5F5F5] mb-3">
          <Input placeholder="Phase Name" value={newPhase.name} onChange={e => setNewPhase({...newPhase, name: e.target.value})}
            className="rounded-none border-[#E5E5E5] h-9 text-sm mb-2" />
          <Input placeholder="Deliverables (comma-separated)" value={newPhase.deliverables}
            onChange={e => setNewPhase({...newPhase, deliverables: e.target.value})}
            className="rounded-none border-[#E5E5E5] h-9 text-sm mb-2" />
          <div className="flex gap-2 mb-2">
            <select value={newPhase.currency || 'INR'} onChange={e => setNewPhase({...newPhase, currency: e.target.value})}
              className="rounded-none border border-[#E5E5E5] h-9 text-sm px-2 bg-white">
              <option value="INR">INR</option><option value="USD">USD</option><option value="EUR">EUR</option><option value="GBP">GBP</option><option value="JPY">JPY</option>
            </select>
            <Input type="number" placeholder="Payment Amount" value={newPhase.payment_amount}
              onChange={e => setNewPhase({...newPhase, payment_amount: e.target.value})}
              className="rounded-none border-[#E5E5E5] h-9 text-sm flex-1" />
          </div>
          <Input placeholder="Notes" value={newPhase.notes} onChange={e => setNewPhase({...newPhase, notes: e.target.value})}
            className="rounded-none border-[#E5E5E5] h-9 text-sm mb-2" />
          <div className="flex gap-2">
            <Button size="sm" onClick={addPhase} className="bg-[#121212] text-white rounded-none text-xs h-8">Add</Button>
            <Button size="sm" variant="outline" onClick={() => setAddingPhase(false)} className="rounded-none text-xs h-8 border-[#121212]">Cancel</Button>
          </div>
        </div>
      )}

      {phases.map((phase, idx) => (
        <div key={phase.phase_number} className="border border-[#E5E5E5] bg-white">
          <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-[#F5F5F5]"
            onClick={() => setExpandedPhase(expandedPhase === phase.phase_number ? null : phase.phase_number)}>
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 flex items-center justify-center border border-[#E5E5E5] text-xs font-bold">
                {phase.phase_number}
              </div>
              <div>
                {editingName === phase.phase_number ? (
                  <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                    <Input value={tempName} onChange={e => setTempName(e.target.value)}
                      className="rounded-none border-[#E5E5E5] h-7 text-sm w-48"
                      onKeyDown={e => e.key === 'Enter' && renamephase(phase.phase_number)} autoFocus />
                    <button onClick={() => renamephase(phase.phase_number)} className="p-1 hover:bg-green-50"><Check size={12} className="text-green-600" /></button>
                    <button onClick={() => { setEditingName(null); setTempName(''); }} className="p-1 hover:bg-red-50"><X size={12} className="text-[#D32F2F]" /></button>
                  </div>
                ) : (
                  <p className="text-sm font-medium">{phase.name}</p>
                )}
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`text-[10px] px-1.5 py-0.5 uppercase font-bold ${statusColor(phase.status)}`}>
                    {phase.status === 'locked' && <Lock size={8} className="inline mr-0.5" />}
                    {phase.status === 'active' && <Unlock size={8} className="inline mr-0.5" />}
                    {phase.status}
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 uppercase font-bold ${paymentColor(phase.payment_status)}`}>
                    Payment: {phase.payment_status}
                  </span>
                  {phase.payment_amount > 0 && (
                    <span className="text-[10px] text-[#5C5C5C]">{phase.currency || 'INR'} {phase.payment_amount?.toLocaleString()}</span>
                  )}
                  {/* Upload count + file names inline */}
                  {(() => {
                    const allFiles = (phase.deliverables || []).flatMap(d => d.file_ids || []);
                    if (allFiles.length === 0) return null;
                    return (
                      <span className="text-[10px] text-[#121212] font-medium flex items-center gap-1">
                        <Upload size={9} className="inline" />
                        {allFiles.length} upload{allFiles.length > 1 ? 's' : ''}
                        <span className="text-[9px] text-[#999] font-normal truncate max-w-[250px]">
                          ({allFiles.map(fId => fileDetails[fId]?.original_filename || '').filter(Boolean).join(', ') || '...'})
                        </span>
                      </span>
                    );
                  })()}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={(e) => { e.stopPropagation(); movePhase(phase.phase_number, 'up'); }}
                className="p-1 hover:bg-gray-100 transition-colors" title="Move Up">
                <ArrowUp size={12} className="text-[#5C5C5C]" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); movePhase(phase.phase_number, 'down'); }}
                className="p-1 hover:bg-gray-100 transition-colors" title="Move Down">
                <ArrowDown size={12} className="text-[#5C5C5C]" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); setEditingName(phase.phase_number); setTempName(phase.name); }}
                className="p-1 hover:bg-blue-50 transition-colors" title="Rename">
                <Edit2 size={12} className="text-[#5C5C5C]" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); deletePhase(phase.phase_number); }}
                className="p-1 hover:bg-red-50 transition-colors" title="Delete">
                <Trash2 size={12} className="text-[#D32F2F]" />
              </button>
              <ChevronRight size={14} className={`transition-transform ${expandedPhase === phase.phase_number ? 'rotate-90' : ''}`} />
            </div>
          </div>

          {expandedPhase === phase.phase_number && (
            <div className="border-t border-[#E5E5E5] p-4 space-y-3">
              {phase.notes && <p className="text-xs text-[#5C5C5C] italic">{phase.notes}</p>}

              <div className="flex gap-2 flex-wrap">
                <Select value={phase.status} onValueChange={val => updatePhase(phase.phase_number, { status: val })}>
                  <SelectTrigger className="rounded-none border-[#E5E5E5] h-8 text-xs w-28"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="locked">Locked</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={phase.payment_status} onValueChange={val => updatePhase(phase.phase_number, { payment_status: val })}>
                  <SelectTrigger className="rounded-none border-[#E5E5E5] h-8 text-xs w-32" data-testid={`phase-payment-status-${phase.phase_number}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="verified">Verified</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={phase.currency || 'INR'} onValueChange={val => updatePhase(phase.phase_number, { currency: val })}>
                  <SelectTrigger className="rounded-none border-[#E5E5E5] h-8 text-xs w-20"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="INR">INR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                    <SelectItem value="JPY">JPY</SelectItem>
                  </SelectContent>
                </Select>
                <Input type="number" placeholder="Amount" className="rounded-none border-[#E5E5E5] h-8 text-xs w-28"
                  defaultValue={phase.payment_amount}
                  onBlur={e => updatePhase(phase.phase_number, { payment_amount: parseFloat(e.target.value) || 0 })} />
                <a href={`/payment?project=${project.project_id}&phase=${phase.phase_number}`} target="_blank" rel="noopener noreferrer">
                  <button className="h-8 px-3 text-[10px] uppercase tracking-wider bg-green-600 text-white hover:bg-green-700 transition-colors">
                    Pay Now
                  </button>
                </a>
              </div>

              <p className="text-xs font-bold uppercase tracking-wider mt-3">Deliverables</p>
              {phase.deliverables?.map((del, dIdx) => {
                const isYtEditing = ytEdit?.phaseNum === phase.phase_number && ytEdit?.dIdx === dIdx;
                return (
                <div key={dIdx} className="py-2 border-b border-[#E5E5E5] last:border-0">
                  <div className="flex items-center justify-end gap-1">
                    {/* YouTube add button */}
                    <button onClick={(e) => {
                      e.stopPropagation();
                      setYtEdit({ phaseNum: phase.phase_number, dIdx });
                      setYtForm({ name: '', url: '' });
                    }} className="text-[9px] border border-red-400 text-red-600 px-1.5 py-0.5 hover:bg-red-600 hover:text-white transition-colors flex items-center gap-0.5" title="Add YouTube link">
                      <svg width="10" height="8" viewBox="0 0 24 18"><path d="M23.5 2.8c-.3-1-1-1.8-2-2C19.6.3 12 .3 12 .3S4.4.3 2.5.8c-1 .3-1.8 1-2 2C0 4.7 0 9 0 9s0 4.3.5 6.2c.3 1 1 1.8 2 2C4.4 17.7 12 17.7 12 17.7s7.6 0 9.5-.5c1-.3 1.8-1 2-2 .5-1.9.5-6.2.5-6.2s0-4.3-.5-6.2z" fill="currentColor"/><path d="M9.6 12.8L15.8 9 9.6 5.2v7.6z" fill="#FFF"/></svg>
                      + YouTube
                    </button>
                    <label className="cursor-pointer">
                      <input type="file" className="hidden"
                        data-testid={`upload-deliverable-${phase.phase_number}-${dIdx}`}
                        onChange={(e) => e.target.files[0] && uploadDeliverableFile(phase.phase_number, dIdx, e.target.files[0])} />
                      <span className="text-[10px] border border-[#121212] px-2 py-1 hover:bg-[#121212] hover:text-white transition-colors cursor-pointer">
                        <Upload size={9} className="inline mr-0.5" /> Upload
                      </span>
                    </label>
                  </div>
                  {/* YouTube add form */}
                  {isYtEditing && (
                    <div className="flex items-center gap-2 mt-2 p-2 border border-red-200 bg-red-50" onClick={e => e.stopPropagation()}>
                      <svg width="16" height="12" viewBox="0 0 24 18" className="flex-shrink-0">
                        <path d="M23.5 2.8c-.3-1-1-1.8-2-2C19.6.3 12 .3 12 .3S4.4.3 2.5.8c-1 .3-1.8 1-2 2C0 4.7 0 9 0 9s0 4.3.5 6.2c.3 1 1 1.8 2 2C4.4 17.7 12 17.7 12 17.7s7.6 0 9.5-.5c1-.3 1.8-1 2-2 .5-1.9.5-6.2.5-6.2s0-4.3-.5-6.2z" fill="#FF0000"/>
                        <path d="M9.6 12.8L15.8 9 9.6 5.2v7.6z" fill="#FFF"/>
                      </svg>
                      <Input placeholder="Video name (e.g. 3D Walkthrough)" value={ytForm.name}
                        onChange={e => setYtForm({...ytForm, name: e.target.value})}
                        className="rounded-none border-[#E5E5E5] h-8 text-xs flex-1" />
                      <Input placeholder="Paste YouTube URL here" value={ytForm.url}
                        onChange={e => setYtForm({...ytForm, url: e.target.value})}
                        className="rounded-none border-[#E5E5E5] h-8 text-xs flex-[2]" />
                      <button onClick={async () => {
                        if (!ytForm.url) return;
                        await axios.put(`${API}/projects/${project.project_id}/phases/${phase.phase_number}/deliverables/${dIdx}/youtube`,
                          { youtube_url: ytForm.url, youtube_name: ytForm.name, action: 'add' }, { withCredentials: true });
                        setYtEdit(null); setYtForm({ name: '', url: '' });
                        onRefresh();
                      }} className="text-[9px] bg-red-600 text-white px-2 py-1.5 hover:bg-red-700 transition-colors flex-shrink-0">Add</button>
                      <button onClick={() => setYtEdit(null)} className="text-[9px] border border-[#E5E5E5] px-2 py-1.5 hover:bg-[#F5F5F5] transition-colors flex-shrink-0">Cancel</button>
                    </div>
                  )}
                  {/* YouTube links list */}
                  {(del.youtube_links || (del.youtube_url ? [{ name: del.youtube_name, url: del.youtube_url }] : [])).map((yt, ytIdx) => yt.url && (
                    <div key={ytIdx} className="flex items-center gap-1.5 ml-6 mt-1 text-[11px]">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
                      <svg width="12" height="9" viewBox="0 0 24 18" className="flex-shrink-0">
                        <path d="M23.5 2.8c-.3-1-1-1.8-2-2C19.6.3 12 .3 12 .3S4.4.3 2.5.8c-1 .3-1.8 1-2 2C0 4.7 0 9 0 9s0 4.3.5 6.2c.3 1 1 1.8 2 2C4.4 17.7 12 17.7 12 17.7s7.6 0 9.5-.5c1-.3 1.8-1 2-2 .5-1.9.5-6.2.5-6.2s0-4.3-.5-6.2z" fill="#FF0000"/>
                        <path d="M9.6 12.8L15.8 9 9.6 5.2v7.6z" fill="#FFF"/>
                      </svg>
                      <a href={yt.url} target="_blank" rel="noopener noreferrer" className="text-red-600 underline truncate max-w-[200px]">
                        {yt.name || 'Watch Animation'}
                      </a>
                      <button onClick={async (e) => {
                        e.stopPropagation();
                        await axios.put(`${API}/projects/${project.project_id}/phases/${phase.phase_number}/deliverables/${dIdx}/youtube`,
                          { action: 'delete', link_index: ytIdx }, { withCredentials: true });
                        onRefresh();
                      }} className="text-[9px] text-[#D32F2F] border border-red-300 px-1 py-0.5 hover:bg-[#D32F2F] hover:text-white transition-colors flex-shrink-0 ml-auto">
                        Delete
                      </button>
                    </div>
                  ))}
                  {/* Uploaded files as bullet list with date, re-upload, delete */}
                  {del.file_ids?.length > 0 && (
                    <ul className="ml-6 mt-1.5 space-y-1.5">
                      {del.file_ids.map(fId => {
                        const f = fileDetails[fId];
                        return (
                          <li key={fId} className="flex items-center gap-1.5 text-[11px] text-[#5C5C5C] flex-wrap">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#121212] flex-shrink-0" />
                            <FileText size={11} className="flex-shrink-0" />
                            <span className="truncate max-w-[180px]">{f?.original_filename || fId}</span>
                            {f?.size && <span className="text-[9px] flex-shrink-0">({(f.size / (1024*1024)).toFixed(1)} MB)</span>}
                            {f?.created_at && <span className="text-[9px] text-[#999] flex-shrink-0">{new Date(f.created_at).toLocaleDateString('en-IN', {day:'numeric',month:'short',year:'numeric'})}</span>}
                            {/* Preview - opens in correct format */}
                            <button onClick={async () => {
                              try {
                                const res = await axios.get(`${API}/files/${fId}/download`, { responseType: 'blob' });
                                const contentType = res.headers['content-type'] || f?.content_type || 'application/octet-stream';
                                const blob = new Blob([res.data], { type: contentType });
                                const url = URL.createObjectURL(blob);
                                window.open(url, '_blank');
                              } catch {}
                            }} className="text-[9px] text-green-700 border border-green-300 px-1.5 py-0.5 hover:bg-green-600 hover:text-white transition-colors flex-shrink-0 ml-auto">
                              Preview
                            </button>
                            {/* Download - saves with correct filename & format */}
                            <button onClick={async () => {
                              try {
                                const res = await axios.get(`${API}/files/${fId}/download`, { responseType: 'blob' });
                                const contentType = res.headers['content-type'] || f?.content_type || 'application/octet-stream';
                                const blob = new Blob([res.data], { type: contentType });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a'); a.href = url; a.download = f?.original_filename || 'file'; a.click(); URL.revokeObjectURL(url);
                              } catch {}
                            }} className="text-[9px] text-[#121212] border border-[#121212] px-1.5 py-0.5 hover:bg-[#121212] hover:text-white transition-colors flex-shrink-0">
                              Download
                            </button>
                            {/* Re-upload / Revision */}
                            <label className="cursor-pointer flex-shrink-0 ml-auto">
                              <input type="file" className="hidden"
                                onChange={(e) => e.target.files[0] && uploadDeliverableFile(phase.phase_number, dIdx, e.target.files[0])} />
                              <span className="text-[9px] text-blue-600 border border-blue-300 px-1.5 py-0.5 hover:bg-blue-600 hover:text-white transition-colors cursor-pointer">
                                Re-upload
                              </span>
                            </label>
                            {/* Delete */}
                            <button onClick={async () => {
                              try {
                                await axios.delete(`${API}/files/${fId}`, { withCredentials: true });
                                onRefresh();
                              } catch {}
                            }} className="text-[9px] text-[#D32F2F] border border-red-300 px-1.5 py-0.5 hover:bg-[#D32F2F] hover:text-white transition-colors flex-shrink-0">
                              Delete
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
              })}
              {uploading && (
                <div>
                  <Progress value={uploadProgress} className="h-1.5 rounded-none" />
                  <p className="text-[10px] text-[#5C5C5C] mt-1">{uploadProgress}%</p>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
      {phases.length === 0 && <p className="text-xs text-[#5C5C5C]">No phases defined</p>}
    </div>
  );
}

function ProjectsTab({ projects, clients, vendors, allUsers = [], onRefresh }) {
  const [showCreate, setShowCreate] = useState(false);
  const defaultPhases = [
    { name: 'Proposal & GFC', amount: '' },
    { name: 'Layout & Civil Drawing', amount: '' },
    { name: 'Floor Drawings (Door-Window & Staircase)', amount: '' },
    { name: 'Civil Drawing for Slab', amount: '' },
  ];
  const [form, setForm] = useState({ title: '', description: '', client_ids: [], client_name: '', no_phases: false, amount: '', phases: [...defaultPhases] });
  const [selectedProject, setSelectedProject] = useState(null);

  const toggleClient = (userId) => {
    const ids = form.client_ids.includes(userId)
      ? form.client_ids.filter(id => id !== userId)
      : [...form.client_ids, userId];
    const names = clients.filter(c => ids.includes(c.user_id)).map(c => c.name).join(', ');
    setForm({ ...form, client_ids: ids, client_name: names });
  };

  const updateFormPhase = (idx, field, value) => {
    const p = [...form.phases];
    p[idx] = { ...p[idx], [field]: value };
    setForm({ ...form, phases: p });
  };
  const addFormPhase = () => setForm({ ...form, phases: [...form.phases, { name: `Phase ${form.phases.length + 1}`, amount: '' }] });
  const removeFormPhase = (idx) => setForm({ ...form, phases: form.phases.filter((_, i) => i !== idx) });

  const createProject = async () => {
    try {
      const payload = {
        title: form.title,
        description: form.description,
        client_id: form.client_ids[0] || null,
        client_ids: form.client_ids,
        client_name: form.client_name,
      };
      if (form.no_phases) {
        payload.no_phases = true;
        if (form.amount) payload.total_amount = parseFloat(form.amount) || 0;
      } else {
        payload.phases = form.phases.map((p, i) => ({
          phase_number: i + 1,
          name: p.name || `Phase ${i + 1}`,
          deliverables: [{ name: p.name || `Deliverable ${i + 1}`, status: 'pending', file_ids: [] }],
          payment_required_before_next: i < form.phases.length - 1,
          payment_amount: parseFloat(p.amount) || 0,
          currency: p.currency || 'INR',
          payment_status: 'pending',
          status: i === 0 ? 'active' : 'locked',
          notes: ''
        }));
      }
      await axios.post(`${API}/projects`, payload, { withCredentials: true });
      setShowCreate(false);
      setForm({ title: '', description: '', client_ids: [], client_name: '', no_phases: false, amount: '', phases: [...defaultPhases] });
      onRefresh();
    } catch (err) { console.error(err); }
  };

  const deleteProject = async (projectId) => {
    try {
      await axios.delete(`${API}/projects/${projectId}`, { withCredentials: true });
      onRefresh();
      setSelectedProject(null);
    } catch {}
  };

  const closeProject = async (projectId) => {
    try {
      await axios.post(`${API}/projects/${projectId}/close`, {}, { withCredentials: true });
      onRefresh();
    } catch {}
  };

  return (
    <div data-testid="admin-projects">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Projects</h2>
        <Button data-testid="create-project-btn" onClick={() => setShowCreate(true)}
          className="bg-[#121212] text-white rounded-none h-10 text-xs tracking-[0.15em] uppercase">
          <Plus size={14} className="mr-2" /> New Project
        </Button>
      </div>

      {showCreate && (
        <div className="border border-[#E5E5E5] bg-white p-6 mb-6">
          <h3 className="text-sm font-bold uppercase tracking-wider mb-4">Create Project</h3>
          <div className="space-y-4">
            <Input data-testid="project-title-input" placeholder="Project Title" value={form.title}
              onChange={e => setForm({...form, title: e.target.value})} className="rounded-none border-[#E5E5E5] h-11" />
            <Textarea data-testid="project-desc-input" placeholder="Description" value={form.description}
              onChange={e => setForm({...form, description: e.target.value})} className="rounded-none border-[#E5E5E5] resize-none" rows={2} />
            <div>
              <p className="text-xs font-bold uppercase tracking-wider mb-2">Assign Clients (select multiple)</p>
              <div className="border border-[#E5E5E5] max-h-36 overflow-y-auto">
                {clients.map(c => (
                  <label key={c.user_id} className="flex items-center gap-2 px-3 py-2 hover:bg-[#F5F5F5] cursor-pointer border-b border-[#E5E5E5] last:border-0">
                    <input type="checkbox" checked={form.client_ids.includes(c.user_id)}
                      onChange={() => toggleClient(c.user_id)} className="w-4 h-4" />
                    <span className="text-sm">{c.name} <span className="text-[#5C5C5C]">({c.email})</span></span>
                  </label>
                ))}
                {clients.length === 0 && <p className="p-3 text-xs text-[#5C5C5C]">No clients yet</p>}
              </div>
              {form.client_ids.length > 0 && (
                <p className="text-[10px] text-[#5C5C5C] mt-1">{form.client_ids.length} client(s) selected</p>
              )}
            </div>
            <div>
              <label className="flex items-center gap-3 mb-3 cursor-pointer">
                <div className="flex gap-2">
                  <button onClick={() => setForm({...form, no_phases: false})}
                    className={`px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${!form.no_phases ? 'bg-[#121212] text-white border-[#121212]' : 'bg-white text-[#5C5C5C] border-[#E5E5E5]'}`}>
                    With Phases
                  </button>
                  <button onClick={() => setForm({...form, no_phases: true})}
                    className={`px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${form.no_phases ? 'bg-[#121212] text-white border-[#121212]' : 'bg-white text-[#5C5C5C] border-[#E5E5E5]'}`}>
                    Without Phases
                  </button>
                </div>
              </label>
              {!form.no_phases && (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-bold uppercase tracking-wider">Phases ({form.phases.length})</p>
                    <button onClick={addFormPhase} className="text-[10px] border border-[#121212] px-3 py-1 hover:bg-[#121212] hover:text-white transition-colors">
                      <Plus size={10} className="inline mr-1" /> Add Phase
                    </button>
                  </div>
                  <div className="space-y-2">
                    {form.phases.map((p, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-[#5C5C5C] w-6 text-center">{i + 1}</span>
                        <Input placeholder={`Phase ${i + 1} name`} value={p.name}
                          onChange={e => updateFormPhase(i, 'name', e.target.value)}
                          className="rounded-none border-[#E5E5E5] h-9 text-sm flex-1" />
                        <select value={p.currency || 'INR'} onChange={e => updateFormPhase(i, 'currency', e.target.value)}
                          className="rounded-none border border-[#E5E5E5] h-9 text-sm px-1 bg-white w-16">
                          <option value="INR">INR</option><option value="USD">USD</option><option value="EUR">EUR</option><option value="GBP">GBP</option><option value="JPY">JPY</option>
                        </select>
                        <Input type="number" placeholder="Amount" value={p.amount}
                          onChange={e => updateFormPhase(i, 'amount', e.target.value)}
                          className="rounded-none border-[#E5E5E5] h-9 text-sm w-28" />
                        {form.phases.length > 1 && (
                          <button onClick={() => removeFormPhase(i)} className="p-1 hover:bg-red-50">
                            <Trash2 size={12} className="text-[#D32F2F]" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
              {form.no_phases && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider mb-2">Project Amount (INR)</p>
                  <Input type="number" placeholder="Total project amount" value={form.amount}
                    onChange={e => setForm({...form, amount: e.target.value})}
                    className="rounded-none border-[#E5E5E5] h-10 max-w-xs" />
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <Button data-testid="save-project-btn" onClick={createProject}
                className="bg-[#121212] text-white rounded-none h-10 text-xs tracking-[0.15em] uppercase">Create</Button>
              <Button variant="outline" onClick={() => setShowCreate(false)}
                className="rounded-none h-10 text-xs tracking-[0.15em] uppercase border-[#121212]">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-1">
        {projects.map(proj => (
          <div key={proj.project_id} className="border border-[#E5E5E5] bg-white">
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1 cursor-pointer" onClick={() => setSelectedProject(selectedProject === proj.project_id ? null : proj.project_id)}>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold">{proj.title}</p>
                    <span className="text-[10px] text-[#5C5C5C] font-mono">{proj.project_id}</span>
                  </div>
                  <p className="text-xs text-[#5C5C5C] mt-1">{proj.description}</p>
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    <Badge variant="outline" className="rounded-none text-[10px] uppercase">{proj.status}</Badge>
                    <span className="text-[10px] text-[#5C5C5C]">{proj.phases?.length || 0} phases</span>
                    {/* All assigned users */}
                    {(() => {
                      const ids = proj.client_ids?.length > 0 ? proj.client_ids : (proj.client_id ? [proj.client_id] : []);
                      const names = ids.map(id => allUsers.find(u => u.user_id === id)?.name || id).filter(Boolean);
                      return names.length > 0 ? names.map((n, i) => (
                        <span key={i} className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 font-medium">{n}</span>
                      )) : <span className="text-[10px] text-[#999]">No client assigned</span>;
                    })()}
                  </div>
                </div>
                <div className="flex gap-1">
                  {proj.status === 'active' && (
                    <Button variant="ghost" size="sm" onClick={() => closeProject(proj.project_id)} title="Close Project">
                      <X size={14} className="text-amber-600" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => deleteProject(proj.project_id)}>
                    <Trash2 size={14} className="text-[#D32F2F]" />
                  </Button>
                </div>
              </div>

              {selectedProject === proj.project_id && (
                <PhaseEditor project={proj} onRefresh={onRefresh} />
              )}
            </div>
          </div>
        ))}
        {projects.length === 0 && (
          <div className="border border-[#E5E5E5] bg-white p-6 text-center">
            <p className="text-sm text-[#5C5C5C]">No projects yet. Create your first project.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function UsersTab({ users, onRefresh, projects = [] }) {
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: 'client', company: '', phone: '', assign_projects: [] });
  const [editingUser, setEditingUser] = useState(null);

  const createUser = async () => {
    try {
      const res = await axios.post(`${API}/admin/users`, form, { withCredentials: true });
      // Assign user to selected projects
      if (form.assign_projects.length > 0 && res.data?.user_id) {
        for (const projId of form.assign_projects) {
          const proj = projects.find(p => p.project_id === projId);
          const currentIds = proj?.client_ids || [];
          if (!currentIds.includes(res.data.user_id)) {
            await axios.put(`${API}/projects/${projId}`, {
              client_ids: [...currentIds, res.data.user_id]
            }, { withCredentials: true });
          }
        }
      }
      setShowCreate(false);
      setForm({ name: '', email: '', role: 'client', company: '', phone: '', assign_projects: [] });
      onRefresh();
    } catch (err) { console.error(err); }
  };

  const toggleProject = (projId) => {
    const ids = form.assign_projects.includes(projId)
      ? form.assign_projects.filter(id => id !== projId)
      : [...form.assign_projects, projId];
    setForm({ ...form, assign_projects: ids });
  };

  const assignUserToProject = async (userId, projId) => {
    const proj = projects.find(p => p.project_id === projId);
    const currentIds = proj?.client_ids || [];
    if (!currentIds.includes(userId)) {
      await axios.put(`${API}/projects/${projId}`, {
        client_ids: [...currentIds, userId]
      }, { withCredentials: true });
      onRefresh();
    }
  };

  const updateUserRole = async (userId, role) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { role }, { withCredentials: true });
      onRefresh();
    } catch {}
  };

  const deleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      onRefresh();
    } catch {}
  };

  const roleColor = (role) => {
    if (role === 'admin') return 'bg-red-100 text-red-800';
    if (role === 'editor') return 'bg-purple-100 text-purple-800';
    if (role === 'viewer') return 'bg-blue-100 text-blue-800';
    if (role === 'vendor' || role === 'contractor' || role === 'subcontractor') return 'bg-amber-100 text-amber-800';
    return 'bg-gray-100 text-gray-600';
  };

  return (
    <div data-testid="admin-users">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">User Management</h2>
        <Button data-testid="create-user-btn" onClick={() => setShowCreate(true)}
          className="bg-[#121212] text-white rounded-none h-10 text-xs tracking-[0.15em] uppercase">
          <UserPlus size={14} className="mr-2" /> Add User
        </Button>
      </div>

      {showCreate && (
        <div className="border border-[#E5E5E5] bg-white p-6 mb-6">
          <h3 className="text-sm font-bold uppercase tracking-wider mb-4">Create User</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input data-testid="user-name" placeholder="Full Name" value={form.name}
              onChange={e => setForm({...form, name: e.target.value})} className="rounded-none border-[#E5E5E5] h-11" />
            <Input data-testid="user-email" type="email" placeholder="Email" value={form.email}
              onChange={e => setForm({...form, email: e.target.value})} className="rounded-none border-[#E5E5E5] h-11" />
            <Input data-testid="user-phone" placeholder="Phone" value={form.phone}
              onChange={e => setForm({...form, phone: e.target.value})} className="rounded-none border-[#E5E5E5] h-11" />
            <Input data-testid="user-company" placeholder="Company" value={form.company}
              onChange={e => setForm({...form, company: e.target.value})} className="rounded-none border-[#E5E5E5] h-11" />
            <Select value={form.role} onValueChange={val => setForm({...form, role: val})}>
              <SelectTrigger data-testid="user-role-select" className="rounded-none border-[#E5E5E5] h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="editor">Editor</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
                <SelectItem value="client">Client</SelectItem>
                <SelectItem value="vendor">Vendor</SelectItem>
                <SelectItem value="contractor">Contractor</SelectItem>
                <SelectItem value="subcontractor">Subcontractor</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* Project Assignment */}
          {['client', 'viewer'].includes(form.role) && projects.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-bold uppercase tracking-wider mb-2">Assign to Projects (optional)</p>
              <div className="border border-[#E5E5E5] max-h-32 overflow-y-auto">
                {projects.map(p => (
                  <label key={p.project_id} className="flex items-center gap-2 px-3 py-2 hover:bg-[#F5F5F5] cursor-pointer border-b border-[#E5E5E5] last:border-0">
                    <input type="checkbox" checked={form.assign_projects.includes(p.project_id)}
                      onChange={() => toggleProject(p.project_id)} className="w-4 h-4" />
                    <span className="text-sm">{p.title} <span className="text-[10px] text-[#5C5C5C] font-mono">{p.project_id}</span></span>
                  </label>
                ))}
              </div>
              {form.assign_projects.length > 0 && (
                <p className="text-[10px] text-[#5C5C5C] mt-1">{form.assign_projects.length} project(s) selected</p>
              )}
            </div>
          )}
          <div className="flex gap-3 mt-4">
            <Button data-testid="save-user-btn" onClick={createUser}
              className="bg-[#121212] text-white rounded-none h-10 text-xs tracking-[0.15em] uppercase">Add User</Button>
            <Button variant="outline" onClick={() => setShowCreate(false)}
              className="rounded-none h-10 text-xs tracking-[0.15em] uppercase border-[#121212]">Cancel</Button>
          </div>
        </div>
      )}

      <div className="border border-[#E5E5E5] bg-white divide-y divide-[#E5E5E5]">
        {users.map(u => {
          const userProjects = projects.filter(p =>
            (p.client_ids || []).includes(u.user_id) || p.client_id === u.user_id
          );
          const isExpanded = editingUser === u.user_id;

          return (
            <div key={u.user_id}>
              <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#F5F5F5] transition-colors"
                onClick={() => setEditingUser(isExpanded ? null : u.user_id)}>
                <div className="flex items-center gap-3">
                  {u.picture ? (
                    <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 bg-[#E5E5E5] flex items-center justify-center text-xs font-bold">
                      {u.name?.charAt(0) || '?'}
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium">{u.name || u.email}</p>
                    <p className="text-xs text-[#5C5C5C]">{u.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {userProjects.length > 0 && (
                    <span className="text-[10px] text-[#5C5C5C] hidden md:inline">{userProjects.length} project(s)</span>
                  )}
                  <Select value={u.role} onValueChange={val => updateUserRole(u.user_id, val)}>
                    <SelectTrigger className={`rounded-none h-7 text-[10px] w-28 uppercase font-bold ${roleColor(u.role)}`}
                      onClick={e => e.stopPropagation()}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="editor">Editor</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                      <SelectItem value="client">Client</SelectItem>
                      <SelectItem value="vendor">Vendor</SelectItem>
                      <SelectItem value="contractor">Contractor</SelectItem>
                      <SelectItem value="subcontractor">Subcontractor</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteUser(u.user_id); }}>
                    <Trash2 size={14} className="text-[#D32F2F]" />
                  </Button>
                  <ChevronRight size={14} className={`transition-transform text-[#5C5C5C] ${isExpanded ? 'rotate-90' : ''}`} />
                </div>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="border-t border-[#E5E5E5] bg-[#FAFAF9] p-4 space-y-4">
                  {/* Login & Account Info */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="border border-[#E5E5E5] bg-white p-3">
                      <p className="text-[10px] text-[#5C5C5C] uppercase tracking-wider font-bold">Account Created</p>
                      <p className="text-xs font-medium mt-1">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}
                      </p>
                    </div>
                    <div className="border border-[#E5E5E5] bg-white p-3">
                      <p className="text-[10px] text-[#5C5C5C] uppercase tracking-wider font-bold">Last Login</p>
                      <p className="text-xs font-medium mt-1">
                        {u.last_login ? new Date(u.last_login).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Never'}
                      </p>
                    </div>
                    <div className="border border-[#E5E5E5] bg-white p-3">
                      <p className="text-[10px] text-[#5C5C5C] uppercase tracking-wider font-bold">Role</p>
                      <p className="text-xs font-medium mt-1 uppercase">{u.role}</p>
                    </div>
                    <div className="border border-[#E5E5E5] bg-white p-3">
                      <p className="text-[10px] text-[#5C5C5C] uppercase tracking-wider font-bold">User ID</p>
                      <p className="text-[10px] font-mono mt-1">{u.user_id}</p>
                    </div>
                  </div>

                  {/* Assigned Projects */}
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider mb-2">
                      Assigned Projects ({userProjects.length})
                    </p>
                    {userProjects.length > 0 ? (
                      <div className="border border-[#E5E5E5] bg-white divide-y divide-[#E5E5E5]">
                        {userProjects.map(p => (
                          <div key={p.project_id} className="flex items-center justify-between px-3 py-2">
                            <div>
                              <span className="text-sm font-medium">{p.title}</span>
                              <span className="text-[10px] text-[#5C5C5C] ml-2 font-mono">{p.project_id}</span>
                            </div>
                            <button onClick={async () => {
                              const newIds = (p.client_ids || []).filter(id => id !== u.user_id);
                              await axios.put(`${API}/projects/${p.project_id}`, { client_ids: newIds }, { withCredentials: true });
                              onRefresh();
                            }}
                              className="text-[10px] text-[#D32F2F] border border-[#D32F2F] px-2 py-1 hover:bg-[#D32F2F] hover:text-white transition-colors">
                              Unassign
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-[#5C5C5C] border border-[#E5E5E5] bg-white p-3">No projects assigned</p>
                    )}
                  </div>

                  {/* Assign New Project */}
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider mb-2">Assign to New Project</p>
                    <div className="border border-[#E5E5E5] bg-white max-h-32 overflow-y-auto">
                      {projects.filter(p => !(p.client_ids || []).includes(u.user_id) && p.client_id !== u.user_id).map(p => (
                        <div key={p.project_id} className="flex items-center justify-between px-3 py-2 hover:bg-[#F5F5F5] border-b border-[#E5E5E5] last:border-0">
                          <span className="text-sm">{p.title}</span>
                          <button onClick={async () => {
                            const newIds = [...(p.client_ids || []), u.user_id];
                            await axios.put(`${API}/projects/${p.project_id}`, { client_ids: newIds }, { withCredentials: true });
                            onRefresh();
                          }}
                            className="text-[10px] text-[#121212] border border-[#121212] px-2 py-1 hover:bg-[#121212] hover:text-white transition-colors">
                            Assign
                          </button>
                        </div>
                      ))}
                      {projects.filter(p => !(p.client_ids || []).includes(u.user_id) && p.client_id !== u.user_id).length === 0 && (
                        <p className="text-xs text-[#5C5C5C] p-3">All projects already assigned</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {users.length === 0 && <p className="p-6 text-sm text-[#5C5C5C] text-center">No users yet</p>}
      </div>
    </div>
  );
}

function VendorsTab({ vendors, onRefresh }) {
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: 'vendor', company: '', phone: '' });

  const createVendor = async () => {
    try {
      await axios.post(`${API}/admin/vendors`, form, { withCredentials: true });
      setShowCreate(false);
      setForm({ name: '', email: '', role: 'vendor', company: '', phone: '' });
      onRefresh();
    } catch {}
  };

  const deleteVendor = async (userId) => {
    try {
      await axios.delete(`${API}/admin/vendors/${userId}`, { withCredentials: true });
      onRefresh();
    } catch {}
  };

  return (
    <div data-testid="admin-vendors">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Vendors & Contractors</h2>
        <Button data-testid="add-vendor-btn" onClick={() => setShowCreate(true)}
          className="bg-[#121212] text-white rounded-none h-10 text-xs tracking-[0.15em] uppercase">
          <UserPlus size={14} className="mr-2" /> Add Vendor
        </Button>
      </div>
      {showCreate && (
        <div className="border border-[#E5E5E5] bg-white p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input data-testid="vendor-name" placeholder="Full Name" value={form.name}
              onChange={e => setForm({...form, name: e.target.value})} className="rounded-none border-[#E5E5E5] h-11" />
            <Input data-testid="vendor-email" type="email" placeholder="Email" value={form.email}
              onChange={e => setForm({...form, email: e.target.value})} className="rounded-none border-[#E5E5E5] h-11" />
            <Input data-testid="vendor-company" placeholder="Company" value={form.company}
              onChange={e => setForm({...form, company: e.target.value})} className="rounded-none border-[#E5E5E5] h-11" />
            <Input data-testid="vendor-phone" placeholder="Phone" value={form.phone}
              onChange={e => setForm({...form, phone: e.target.value})} className="rounded-none border-[#E5E5E5] h-11" />
            <Select value={form.role} onValueChange={val => setForm({...form, role: val})}>
              <SelectTrigger className="rounded-none border-[#E5E5E5] h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="vendor">Vendor</SelectItem>
                <SelectItem value="contractor">Contractor</SelectItem>
                <SelectItem value="subcontractor">Subcontractor</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-3 mt-4">
            <Button data-testid="save-vendor-btn" onClick={createVendor}
              className="bg-[#121212] text-white rounded-none h-10 text-xs tracking-[0.15em] uppercase">Add Vendor</Button>
            <Button variant="outline" onClick={() => setShowCreate(false)}
              className="rounded-none h-10 text-xs tracking-[0.15em] uppercase border-[#121212]">Cancel</Button>
          </div>
        </div>
      )}
      <div className="border border-[#E5E5E5] bg-white divide-y divide-[#E5E5E5]">
        {vendors.map(v => (
          <div key={v.user_id} className="flex items-center justify-between p-4">
            <div>
              <p className="text-sm font-medium">{v.name}</p>
              <p className="text-xs text-[#5C5C5C]">{v.email} {v.company ? `| ${v.company}` : ''}</p>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="rounded-none text-[10px] uppercase">{v.role}</Badge>
              <Button variant="ghost" size="sm" onClick={() => deleteVendor(v.user_id)}>
                <Trash2 size={14} className="text-[#D32F2F]" />
              </Button>
            </div>
          </div>
        ))}
        {vendors.length === 0 && <p className="p-6 text-sm text-[#5C5C5C] text-center">No vendors added yet</p>}
      </div>
    </div>
  );
}

function PaymentsTab({ payments, projects, onRefresh }) {
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ project_id: '', amount: '', description: '', currency: 'INR' });
  const [qrSettings, setQrSettings] = useState({ upi_id: '', merchant_name: 'S.A.I. Design Group' });
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/admin/qr-settings`, { withCredentials: true });
        setQrSettings(res.data);
      } catch {}
    };
    load();
  }, []);

  const createPayment = async () => {
    try {
      await axios.post(`${API}/payments`, { ...form, amount: parseFloat(form.amount) }, { withCredentials: true });
      setShowCreate(false);
      setForm({ project_id: '', amount: '', description: '', currency: 'INR' });
      onRefresh();
    } catch {}
  };

  const updatePaymentStatus = async (paymentId, status) => {
    try {
      await axios.put(`${API}/payments/${paymentId}`, { status }, { withCredentials: true });
      onRefresh();
    } catch {}
  };

  return (
    <div data-testid="admin-payments">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Payments</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowQR(!showQR)} data-testid="show-qr-btn"
            className="rounded-none h-10 text-xs tracking-[0.15em] uppercase border-[#121212]">Payment QR</Button>
          <Button onClick={() => setShowCreate(true)} data-testid="create-payment-btn"
            className="bg-[#121212] text-white rounded-none h-10 text-xs tracking-[0.15em] uppercase">
            <Plus size={14} className="mr-2" /> Create Invoice
          </Button>
        </div>
      </div>
      {showQR && (
        <div className="border border-[#E5E5E5] bg-white p-6 mb-6 grid md:grid-cols-2 gap-6">
          <div>
            {qrSettings.upi_id ? (
              <div className="flex flex-col items-center">
                <div className="border border-[#E5E5E5] p-4">
                  <QRCode value={`upi://pay?pa=${qrSettings.upi_id}&pn=${encodeURIComponent(qrSettings.merchant_name)}`} size={200} level="M" />
                </div>
                <p className="text-xs text-[#5C5C5C] mt-3">{qrSettings.upi_id}</p>
              </div>
            ) : <p className="text-sm text-[#5C5C5C]">Set UPI ID in Settings</p>}
          </div>
          <div>
            <p className="text-xs text-[#5C5C5C] mb-2">Upload custom QR:</p>
            <label className="cursor-pointer"><input type="file" accept="image/*" className="hidden" data-testid="upload-qr-image"
              onChange={async (e) => {
                if (!e.target.files[0]) return;
                const fd = new FormData(); fd.append('file', e.target.files[0]);
                await axios.post(`${API}/admin/qr-image`, fd, { withCredentials: true });
              }} />
              <span className="inline-flex items-center gap-1 text-xs border border-[#121212] px-4 py-2 hover:bg-[#121212] hover:text-white transition-colors cursor-pointer">
                <Upload size={12} /> Upload QR
              </span>
            </label>
          </div>
        </div>
      )}
      {showCreate && (
        <div className="border border-[#E5E5E5] bg-white p-6 mb-6 space-y-4">
          <Select onValueChange={val => setForm({...form, project_id: val})}>
            <SelectTrigger className="rounded-none border-[#E5E5E5] h-11"><SelectValue placeholder="Select Project" /></SelectTrigger>
            <SelectContent>{projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.title}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="number" placeholder="Amount" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} className="rounded-none border-[#E5E5E5] h-11" />
          <Select value={form.currency} onValueChange={val => setForm({...form, currency: val})}>
            <SelectTrigger className="rounded-none border-[#E5E5E5] h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="INR">INR (Rupees)</SelectItem>
              <SelectItem value="USD">USD (Dollar)</SelectItem>
              <SelectItem value="EUR">EUR (Euro)</SelectItem>
              <SelectItem value="GBP">GBP (Pound)</SelectItem>
              <SelectItem value="JPY">JPY (Yen)</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="rounded-none border-[#E5E5E5] h-11" />
          <div className="flex gap-3">
            <Button onClick={createPayment} className="bg-[#121212] text-white rounded-none h-10 text-xs tracking-[0.15em] uppercase">Create</Button>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-none h-10 text-xs tracking-[0.15em] uppercase border-[#121212]">Cancel</Button>
          </div>
        </div>
      )}
      <div className="border border-[#E5E5E5] bg-white divide-y divide-[#E5E5E5]">
        {payments.map(pay => (
          <div key={pay.payment_id} className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">{pay.currency || 'INR'} {pay.amount?.toLocaleString()}</p>
              <p className="text-xs text-[#5C5C5C]">{pay.description}</p>
              <p className="text-[10px] text-[#5C5C5C] mt-1">{new Date(pay.created_at).toLocaleDateString()}</p>
            </div>
            <Select value={pay.status} onValueChange={val => updatePaymentStatus(pay.payment_id, val)}>
              <SelectTrigger className="rounded-none border-[#E5E5E5] h-8 text-xs w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="proof_uploaded">Proof Uploaded</SelectItem>
                <SelectItem value="verified">Verified</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        ))}
        {payments.length === 0 && <p className="p-6 text-sm text-[#5C5C5C] text-center">No payments yet</p>}
      </div>
    </div>
  );
}

function SettingsTab() {
  const [upiId, setUpiId] = useState('');
  const [merchantName, setMerchantName] = useState('S.A.I. Design Group');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/admin/qr-settings`, { withCredentials: true });
        setUpiId(res.data.upi_id || '');
        setMerchantName(res.data.merchant_name || 'S.A.I. Design Group');
      } catch {}
    };
    load();
  }, []);

  const save = async () => {
    try {
      await axios.post(`${API}/admin/qr-settings`, { upi_id: upiId, merchant_name: merchantName }, { withCredentials: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {}
  };

  const cleanup = async () => {
    try {
      const res = await axios.post(`${API}/admin/cleanup`, {}, { withCredentials: true });
      alert(res.data.message);
    } catch {}
  };

  return (
    <div data-testid="admin-settings">
      <h2 className="text-xl font-bold mb-6">Settings</h2>
      <div className="space-y-8">
        <div className="border border-[#E5E5E5] bg-white p-6">
          <h3 className="text-sm font-bold uppercase tracking-wider mb-4">Payment QR Settings</h3>
          <div className="space-y-4 max-w-md">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#5C5C5C] mb-2 block">UPI ID</label>
              <Input data-testid="settings-upi-id" placeholder="yourname@paytm" value={upiId}
                onChange={e => setUpiId(e.target.value)} className="rounded-none border-[#E5E5E5] h-11" />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#5C5C5C] mb-2 block">Merchant Name</label>
              <Input value={merchantName} onChange={e => setMerchantName(e.target.value)} className="rounded-none border-[#E5E5E5] h-11" />
            </div>
            <Button data-testid="save-settings-btn" onClick={save}
              className="bg-[#121212] text-white rounded-none h-10 text-xs tracking-[0.15em] uppercase">
              {saved ? 'Saved!' : 'Save Settings'}
            </Button>
          </div>
        </div>
        <div className="border border-[#E5E5E5] bg-white p-6">
          <h3 className="text-sm font-bold uppercase tracking-wider mb-4">Data Cleanup</h3>
          <p className="text-sm text-[#5C5C5C] mb-4">Archive completed projects older than 1 year.</p>
          <Button onClick={cleanup} variant="outline" data-testid="cleanup-btn"
            className="rounded-none h-10 text-xs tracking-[0.15em] uppercase border-[#D32F2F] text-[#D32F2F] hover:bg-[#D32F2F] hover:text-white">
            <Archive size={14} className="mr-2" /> Run Cleanup
          </Button>
        </div>
      </div>
    </div>
  );
}

function GalleryTab() {
  const [uploading, setUploading] = useState(false);
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/gallery`); setItems(r.data); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const uploadGallery = async (file) => {
    setUploading(true);
    const fd = new FormData(); fd.append('file', file); fd.append('title', file.name.split('.')[0]);
    try { await axios.post(`${API}/admin/gallery`, fd, { withCredentials: true }); load(); } catch {} finally { setUploading(false); }
  };

  const deleteItem = async (id) => {
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, { withCredentials: true });
      load();
    } catch {}
  };

  return (
    <div data-testid="admin-gallery">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Public Gallery ({items.length} images)</h2>
        <label className="cursor-pointer"><input type="file" accept="image/*" className="hidden"
          onChange={(e) => e.target.files[0] && uploadGallery(e.target.files[0])} />
          <span className="inline-flex items-center gap-2 bg-[#121212] text-white px-4 py-2.5 text-xs tracking-[0.15em] uppercase cursor-pointer hover:bg-[#2A2A2A]">
            <Upload size={14} /> {uploading ? 'Uploading...' : 'Add Image'}
          </span>
        </label>
      </div>
      <p className="text-sm text-[#5C5C5C] mb-6">These images appear in the <strong>Featured Projects</strong> gallery on the landing page with a grayscale-to-color hover effect.</p>
      {items.length === 0 ? (
        <div className="border border-[#E5E5E5] bg-white p-12 text-center">
          <ImageIcon size={40} className="mx-auto text-[#E5E5E5] mb-4" strokeWidth={1} />
          <p className="text-sm text-[#5C5C5C]">No gallery images yet. Upload your first image.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {items.map(item => (
            <div key={item.gallery_id} className="border border-[#E5E5E5] bg-white overflow-hidden group relative">
              <img src={`${API}/gallery/${item.gallery_id}/image`} alt={item.title}
                className="w-full h-40 object-cover" />
              <button onClick={() => deleteItem(item.gallery_id)}
                className="absolute top-2 right-2 bg-white/90 hover:bg-red-50 p-1.5 opacity-0 group-hover:opacity-100 transition-opacity border border-[#E5E5E5]"
                title="Delete image">
                <Trash2 size={14} className="text-[#D32F2F]" />
              </button>
              <div className="p-2 border-t border-[#E5E5E5]">
                <p className="text-xs font-bold truncate">{item.title}</p>
                <p className="text-[10px] text-[#5C5C5C]">{new Date(item.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ====== CAREERS ADMIN ======
function CareersAdmin() {
  const [vacancies, setVacancies] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', department: '', location: '', type: 'Full-time', description: '', responsibilities: '', requirements: '' });
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/careers/all`, { withCredentials: true }); setVacancies(r.data); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const data = {
      ...form,
      responsibilities: form.responsibilities.split('\n').filter(Boolean),
      requirements: form.requirements.split('\n').filter(Boolean)
    };
    try {
      if (editing) {
        await axios.put(`${API}/admin/careers/${editing}`, data, { withCredentials: true });
        setEditing(null);
      } else {
        await axios.post(`${API}/admin/careers`, data, { withCredentials: true });
      }
      setShowCreate(false);
      setForm({ title: '', department: '', location: '', type: 'Full-time', description: '', responsibilities: '', requirements: '' });
      load();
    } catch {}
  };

  const del = async (id) => {
    await axios.delete(`${API}/admin/careers/${id}`, { withCredentials: true });
    load();
  };

  const startEdit = (v) => {
    setForm({ title: v.title, department: v.department || '', location: v.location || '', type: v.type || 'Full-time',
      description: v.description || '', responsibilities: (v.responsibilities || []).join('\n'), requirements: (v.requirements || []).join('\n') });
    setEditing(v.vacancy_id);
    setShowCreate(true);
  };

  return (
    <div data-testid="admin-careers">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Careers / Vacancies</h2>
        <Button onClick={() => { setShowCreate(true); setEditing(null); setForm({ title: '', department: '', location: '', type: 'Full-time', description: '', responsibilities: '', requirements: '' }); }}
          className="bg-[#121212] text-white rounded-none h-10 text-xs tracking-[0.15em] uppercase" data-testid="create-vacancy-btn">
          <Plus size={14} className="mr-2" /> New Vacancy
        </Button>
      </div>
      {showCreate && (
        <div className="border border-[#E5E5E5] bg-white p-6 mb-6 space-y-3">
          <h3 className="text-sm font-bold uppercase tracking-wider mb-2">{editing ? 'Edit Vacancy' : 'Create Vacancy'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input placeholder="Job Title (e.g. Interior Designer)" value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="rounded-none border-[#E5E5E5] h-10" data-testid="vacancy-title" />
            <Input placeholder="Department" value={form.department} onChange={e => setForm({...form, department: e.target.value})} className="rounded-none border-[#E5E5E5] h-10" />
            <Input placeholder="Location" value={form.location} onChange={e => setForm({...form, location: e.target.value})} className="rounded-none border-[#E5E5E5] h-10" />
            <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
              <SelectTrigger className="rounded-none border-[#E5E5E5] h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Full-time">Full-time</SelectItem>
                <SelectItem value="Part-time">Part-time</SelectItem>
                <SelectItem value="Contract">Contract</SelectItem>
                <SelectItem value="Internship">Internship</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Textarea placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="rounded-none border-[#E5E5E5] resize-none" rows={2} />
          <Textarea placeholder="Roles & Responsibilities (one per line)" value={form.responsibilities} onChange={e => setForm({...form, responsibilities: e.target.value})} className="rounded-none border-[#E5E5E5] resize-none" rows={3} />
          <Textarea placeholder="Requirements (one per line)" value={form.requirements} onChange={e => setForm({...form, requirements: e.target.value})} className="rounded-none border-[#E5E5E5] resize-none" rows={3} />
          <div className="flex gap-3">
            <Button onClick={save} className="bg-[#121212] text-white rounded-none h-10 text-xs tracking-[0.15em] uppercase">{editing ? 'Update' : 'Create'}</Button>
            <Button variant="outline" onClick={() => { setShowCreate(false); setEditing(null); }} className="rounded-none h-10 text-xs tracking-[0.15em] uppercase border-[#121212]">Cancel</Button>
          </div>
        </div>
      )}
      <div className="border border-[#E5E5E5] bg-white divide-y divide-[#E5E5E5]">
        {vacancies.map(v => (
          <div key={v.vacancy_id} className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-bold">{v.title}</p>
              <p className="text-xs text-[#5C5C5C]">{v.department} | {v.location} | {v.type}</p>
            </div>
            <div className="flex gap-2">
              <Badge variant={v.is_active ? 'default' : 'secondary'} className="rounded-none text-[10px] uppercase">{v.is_active ? 'Active' : 'Closed'}</Badge>
              <Button variant="ghost" size="sm" onClick={() => startEdit(v)}><Edit2 size={14} /></Button>
              <Button variant="ghost" size="sm" onClick={() => del(v.vacancy_id)}><Trash2 size={14} className="text-[#D32F2F]" /></Button>
            </div>
          </div>
        ))}
        {vacancies.length === 0 && <p className="p-6 text-sm text-[#5C5C5C] text-center">No vacancies yet. Create your first job posting.</p>}
      </div>
    </div>
  );
}

// ====== TEAM ADMIN ======
function TeamAdmin() {
  const [members, setMembers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', role: '', bio: '', is_founder: false });
  const [photoFile, setPhotoFile] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/team`); setMembers(r.data); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      let memberId;
      if (editing) {
        await axios.put(`${API}/admin/team/${editing}`, form, { withCredentials: true });
        memberId = editing;
        setEditing(null);
      } else {
        const res = await axios.post(`${API}/admin/team`, form, { withCredentials: true });
        memberId = res.data.member_id;
      }
      // Upload photo if selected
      if (photoFile && memberId) {
        const fd = new FormData(); fd.append('file', photoFile);
        await axios.post(`${API}/admin/team/${memberId}/photo`, fd, { withCredentials: true });
      }
      setShowCreate(false);
      setForm({ name: '', role: '', bio: '', is_founder: false });
      setPhotoFile(null);
      load();
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.detail || 'Please try again'));
    }
  };

  const del = async (id) => {
    await axios.delete(`${API}/admin/team/${id}`, { withCredentials: true });
    load();
  };

  const uploadPhoto = async (memberId, file) => {
    const fd = new FormData(); fd.append('file', file);
    await axios.post(`${API}/admin/team/${memberId}/photo`, fd, { withCredentials: true });
    load();
  };

  return (
    <div data-testid="admin-team">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Team Members</h2>
        <Button onClick={() => { setShowCreate(true); setEditing(null); setForm({ name: '', role: '', bio: '', is_founder: false }); setPhotoFile(null); }}
          className="bg-[#121212] text-white rounded-none h-10 text-xs tracking-[0.15em] uppercase" data-testid="create-team-btn">
          <Plus size={14} className="mr-2" /> Add Member
        </Button>
      </div>
      {showCreate && (
        <div className="border border-[#E5E5E5] bg-white p-6 mb-6 space-y-3">
          <h3 className="text-sm font-bold uppercase tracking-wider mb-2">{editing ? 'Edit Member' : 'Add Team Member'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input placeholder="Full Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-none border-[#E5E5E5] h-10" data-testid="team-name" />
            <Input placeholder="Role (e.g. Founder, Senior Architect)" value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="rounded-none border-[#E5E5E5] h-10" />
            <div>
              <label className="text-[10px] text-[#5C5C5C] block mb-1 uppercase tracking-wider font-bold">Passport Size Photo</label>
              <label className="flex items-center gap-2 cursor-pointer border border-[#E5E5E5] px-3 py-2 hover:bg-[#F5F5F5] transition-colors">
                <Upload size={14} className="text-[#5C5C5C]" />
                <span className="text-sm text-[#5C5C5C]">{photoFile ? photoFile.name : 'Choose photo...'}</span>
                <input type="file" accept="image/*" className="hidden" data-testid="team-photo-upload"
                  onChange={e => setPhotoFile(e.target.files[0] || null)} />
              </label>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_founder} onChange={e => setForm({...form, is_founder: e.target.checked})} className="w-4 h-4" />
              <span className="text-sm">Founder / Leadership</span>
            </label>
          </div>
          <Textarea placeholder="Bio (optional)" value={form.bio} onChange={e => setForm({...form, bio: e.target.value})} className="rounded-none border-[#E5E5E5] resize-none" rows={2} />
          <div className="flex gap-3">
            <Button onClick={save} className="bg-[#121212] text-white rounded-none h-10 text-xs tracking-[0.15em] uppercase">{editing ? 'Update' : 'Add'}</Button>
            <Button variant="outline" onClick={() => { setShowCreate(false); setEditing(null); }} className="rounded-none h-10 text-xs tracking-[0.15em] uppercase border-[#121212]">Cancel</Button>
          </div>
        </div>
      )}
      <div className="border border-[#E5E5E5] bg-white divide-y divide-[#E5E5E5]">
        {members.map(m => (
          <div key={m.member_id} className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#E5E5E5] flex items-center justify-center text-xs font-bold overflow-hidden">
                {(m.picture_url || m.picture_path) ? (
                  <img src={m.picture_url || `${API}/team/${m.member_id}/photo`} alt="" className="w-full h-full object-cover" />
                ) : m.name?.charAt(0) || '?'}
              </div>
              <div>
                <p className="text-sm font-bold">{m.name}</p>
                <p className="text-xs text-[#5C5C5C]">{m.role} {m.is_founder ? '(Founder)' : ''}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadPhoto(m.member_id, e.target.files[0])} />
                <span className="text-[10px] border border-[#121212] px-2 py-1 hover:bg-[#121212] hover:text-white transition-colors cursor-pointer"><Upload size={10} className="inline mr-1" />Photo</span>
              </label>
              <Button variant="ghost" size="sm" onClick={() => { setForm({ name: m.name, role: m.role, bio: m.bio || '', is_founder: m.is_founder || false }); setEditing(m.member_id); setPhotoFile(null); setShowCreate(true); }}><Edit2 size={14} /></Button>
              <Button variant="ghost" size="sm" onClick={() => del(m.member_id)}><Trash2 size={14} className="text-[#D32F2F]" /></Button>
            </div>
          </div>
        ))}
        {members.length === 0 && <p className="p-6 text-sm text-[#5C5C5C] text-center">No team members yet. Add your founder and team.</p>}
      </div>
    </div>
  );
}

// ====== HONORS ADMIN ======
function HonorsAdmin() {
  const [honors, setHonors] = useState([]);
  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/honors`); setHonors(r.data); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    const fd = new FormData(); fd.append('file', file); fd.append('title', file.name.split('.')[0]);
    await axios.post(`${API}/admin/honors`, fd, { withCredentials: true });
    load();
  };

  const del = async (id) => {
    await axios.delete(`${API}/admin/honors/${id}`, { withCredentials: true });
    load();
  };

  return (
    <div data-testid="admin-honors">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Projects & Honors</h2>
        <label className="cursor-pointer"><input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
          <span className="inline-flex items-center gap-2 bg-[#121212] text-white px-4 py-2.5 text-xs tracking-[0.15em] uppercase cursor-pointer hover:bg-[#2A2A2A]">
            <Upload size={14} /> Add Image
          </span>
        </label>
      </div>
      <p className="text-sm text-[#5C5C5C] mb-4">Upload project highlights or honors received. These appear in the sliding gallery on the homepage.</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {honors.map(h => (
          <div key={h.honor_id} className="border border-[#E5E5E5] bg-white overflow-hidden group relative">
            <img src={`${API}/honors/${h.honor_id}/image`} alt={h.title} className="w-full h-32 object-cover" />
            <p className="text-xs p-2 font-bold truncate">{h.title}</p>
            <button onClick={() => del(h.honor_id)} className="absolute top-1 right-1 bg-white/80 p-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Trash2 size={12} className="text-[#D32F2F]" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState('overview');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [payments, setPayments] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const [projRes, usersRes, clientRes, vendorRes, payRes] = await Promise.all([
        axios.get(`${API}/projects`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/clients`, { withCredentials: true }),
        axios.get(`${API}/admin/vendors`, { withCredentials: true }),
        axios.get(`${API}/payments`, { withCredentials: true })
      ]);
      setProjects(projRes.data);
      setAllUsers(usersRes.data);
      setClients(clientRes.data);
      setVendors(vendorRes.data);
      setPayments(payRes.data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleLogout = async () => { await logout(); navigate('/'); };

  return (
    <div className="flex min-h-screen bg-[#FDFCFB]">
      <AdminSidebar active={active} setActive={setActive} onLogout={handleLogout} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <main className="flex-1 min-w-0">
        <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-[#E5E5E5] px-4 py-3 flex items-center justify-between">
          <button onClick={() => setMobileOpen(true)} data-testid="admin-mobile-menu"><Menu size={24} /></button>
          <span className="text-sm font-bold">Admin Panel</span>
          <div />
        </div>
        <div className="p-6 lg:p-10 max-w-6xl">
          <div className="mb-6">
            <p className="text-xs text-[#5C5C5C]">Welcome back, {user?.name}</p>
          </div>
          {active === 'overview' && <OverviewTab projects={projects} users={allUsers} vendors={vendors} payments={payments} />}
          {active === 'projects' && <ProjectsTab projects={projects} clients={clients} vendors={vendors} allUsers={allUsers} onRefresh={loadData} />}
          {active === 'users' && <UsersTab users={allUsers} onRefresh={loadData} projects={projects} />}
          {active === 'vendors' && <VendorsTab vendors={vendors} onRefresh={loadData} />}
          {active === 'payments' && <PaymentsTab payments={payments} projects={projects} onRefresh={loadData} />}
          {active === 'gallery' && <GalleryTab />}
          {active === 'careers' && <CareersAdmin />}
          {active === 'team' && <TeamAdmin />}
          {active === 'honors' && <HonorsAdmin />}
          {active === 'settings' && <SettingsTab />}
        </div>
      </main>
    </div>
  );
}
